/**
 * Bringing a payment or e-mandate window home without navigating the tab away.
 *
 * The investor dashboard can afford a full-page redirect: it re-reads
 * everything it shows from the API when the callback lands. The Portfolio
 * Planner cannot. A generated plan lives in memory only — `generate` returns no
 * id and there is no endpoint to fetch one back — so navigating the tab to a
 * payment gateway loses the document the admin is working from.
 *
 * A popup solves that. The provider still gets a real browser window (payment
 * and bank pages send X-Frame-Options: DENY, so an iframe is not an option),
 * while the opener keeps the plan on screen and makes every follow-up call.
 *
 * The window that comes back lands on /mf-payment-return — the same bridge the
 * dashboard's redirect flow uses, and the same route the POST-callback service
 * worker rewrites to a GET. In popup mode that page hands its parameters to the
 * opener and closes itself instead of redirecting.
 */

/** Message a returning popup posts to its opener. */
export const MF_POPUP_MESSAGE = "smest-admin-mf-order-return";

/**
 * Marker the bridge reads to choose the popup branch over its redirect.
 *
 * Deliberately NOT prefixed `mf_admin_`: `buildAdminPostbackUrl` in
 * mfAdminInvestments strips every `mf_admin_*` key off the return address
 * before it echoes it back, so a marker with that prefix would never survive
 * the round trip.
 */
export const MF_POPUP_FLAG = "mf_popup";

export interface MFPopupReturn {
  type: typeof MF_POPUP_MESSAGE;
  params: Record<string, string>;
}

/**
 * Opens the popup NOW, blank.
 *
 * Popup blockers only allow `window.open` inside the gesture that triggered it,
 * and the provider URL is three API calls away. So the window is opened empty
 * on the click and pointed at the provider once the URL arrives.
 */
export const openBlankPopup = (name: string): Window | null => {
  if (typeof window === "undefined") return null;
  const width = 620;
  const height = 780;
  const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
  const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);
  const popup = window.open(
    "",
    name,
    [
      "popup=yes",
      `width=${width}`,
      `height=${height}`,
      `left=${Math.round(left)}`,
      `top=${Math.round(top)}`,
      "resizable=yes",
      "scrollbars=yes",
    ].join(",")
  );
  if (popup) {
    try {
      popup.document.write(
        '<!doctype html><meta charset="utf-8"><title>Opening secure page…</title>' +
          '<body style="margin:0;display:grid;place-items:center;height:100vh;' +
          'font:600 14px/1.5 Jura,system-ui,sans-serif;color:#39406a;background:#f6f7fb">' +
          "<p>Opening the secure page…</p></body>"
      );
      popup.document.close();
    } catch (error) {
      // A cross-origin document is already loading; nothing to write into.
    }
    popup.focus();
  }
  return popup;
};

/**
 * The URL the provider is told to send the popup back to.
 *
 * `returnPath` is what the bridge validates and echoes back; keeping the
 * popup marker inside it is what tells the bridge not to redirect.
 */
export const popupReturnUrl = (returnPath: string): string => {
  const target = new URL(returnPath, window.location.origin);
  target.searchParams.set(MF_POPUP_FLAG, "1");
  return target.toString();
};

/**
 * Resolves when the popup comes home, rejects if it is closed first.
 *
 * The closed-poll matters: an operator who abandons the bank page and shuts the
 * window would otherwise leave the row spinning for ever.
 */
export const awaitPopupReturn = (
  popup: Window,
  signal?: { cancelled: boolean }
): Promise<Record<string, string>> =>
  new Promise((resolve, reject) => {
    let settled = false;

    const cleanup = () => {
      window.removeEventListener("message", onMessage);
      window.clearInterval(poll);
    };

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data as MFPopupReturn | undefined;
      if (!data || data.type !== MF_POPUP_MESSAGE) return;
      settled = true;
      cleanup();
      resolve(data.params || {});
    };

    window.addEventListener("message", onMessage);

    const poll = window.setInterval(() => {
      if (signal?.cancelled) {
        cleanup();
        if (!popup.closed) popup.close();
        reject(new Error("Cancelled."));
        return;
      }
      if (!settled && popup.closed) {
        cleanup();
        reject(
          new Error(
            "The authorization window was closed before it finished. Check the investor's Orders before trying this line again."
          )
        );
      }
    }, 600);
  });
